import { create } from 'zustand';
import { todayPST } from '../utils/format';
import { getBestGameRecommendation, sortRecommendationsByPriority } from '../utils/recommendations';
import type {
  BetRecommendation,
  Bet,
  BankrollSettings,
  ExposureReport,
  Alert,
  LiveGameData,
  WatchlistItem,
  AgentActivityEvent,
  IntelligenceSignal,
  SystemHealthStatus,
  MarketVolatility,
  SignalFilter,
  Parlay,
  ParlayLeg,
} from '../types';

import type { GameContext } from '../api/client';
import { gamesApi, advisorApi, betsApi, bankrollApi, alertsApi, watchlistApi, parlaysApi } from '../api/client';

interface AppState {
  // ── Date ────────────────────────────────────
  selectedDate: string;
  setSelectedDate: (date: string) => void;

  // ── Games ────────────────────────────────────
  games: GameContext[];
  gamesLoading: boolean;
  gamesError: string | null;
  liveGames: Record<number, LiveGameData>;
  fetchGames: (date?: string) => Promise<void>;
  updateLiveGame: (data: LiveGameData) => void;

  // ── Recommendations ───────────────────────────
  recommendations: BetRecommendation[];
  recsLoading: boolean;
  recsError: string | null;
  lastRecsDate: string | null;
  fetchRecommendations: (date?: string, refresh?: boolean) => Promise<void>;

  // ── Bets ──────────────────────────────────────
  bets: Bet[];
  betsLoading: boolean;
  fetchBets: () => Promise<void>;
  placeBet: (data: Parameters<typeof betsApi.place>[0]) => Promise<Bet>;
  settleBet: (
    id: string,
    result: 'won' | 'lost' | 'push' | 'void',
    closingOdds?: number,
  ) => Promise<void>;
  removeBet: (id: string) => Promise<void>;

  // ── Bankroll ─────────────────────────────────
  bankrollSettings: BankrollSettings | null;
  exposure: ExposureReport | null;
  bankrollLoading: boolean;
  fetchBankroll: () => Promise<void>;
  updateBankrollSettings: (settings: Partial<BankrollSettings>) => Promise<void>;

  // ── User session reset ────────────────────────
  /** Clears all user-specific data. Call on sign-out or user switch. */
  resetUserData: () => void;

  // ── Alerts ────────────────────────────────────
  alerts: Alert[];
  unreadCount: number;
  fetchAlerts: () => Promise<void>;
  addAlert: (alert: Alert) => void;
  markAlertRead: (id: number) => void;
  markAllAlertsRead: () => void;

  // ── Watchlist ─────────────────────────────────
  watchlist: WatchlistItem[];
  fetchWatchlist: () => Promise<void>;
  addToWatchlist: (gamePk: number, recommendationId?: string) => Promise<void>;
  removeFromWatchlist: (gamePk: number) => Promise<void>;
  isWatched: (gamePk: number) => boolean;

  // ── UI state ──────────────────────────────────
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** Derived: 'connected' if either SSE or Realtime channel is up. */
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  /** SSE (EventSource) connection status — set by useSSE. */
  sseStatus: 'connected' | 'disconnected' | 'connecting';
  setSSEStatus: (status: 'connected' | 'disconnected' | 'connecting') => void;
  /** Supabase Realtime (WebSocket) status — set by useOrgRealtime. */
  realtimeStatus: 'connected' | 'disconnected' | 'connecting';
  setRealtimeStatus: (status: 'connected' | 'disconnected' | 'connecting') => void;
  /** @deprecated use setSSEStatus or setRealtimeStatus instead. */
  setConnectionStatus: (status: 'connected' | 'disconnected' | 'connecting') => void;
  lastRefreshAt: string | null;
  setLastRefreshAt: (ts: string) => void;

  // ── Mobile navigation state ───────────────────
  mobileDrawerOpen: boolean;
  mobileDrawerTab: 'nav' | 'module';
  setMobileDrawerOpen: (open: boolean, tab?: 'nav' | 'module') => void;

  // ── Module / league context ───────────────────
  activeModuleId: string;
  activeLeagueId: string;
  activeSportId: string;
  setActiveContext: (moduleId: string, leagueId: string, sportId: string) => void;
  expandedGamePk: string | null;
  setExpandedGamePk: (gamePk: string | null) => void;

  // ── Intelligence bar ──────────────────────────
  intelligenceBarCollapsed: boolean;
  toggleIntelligenceBar: () => void;

  // ── Intelligence Panel ────────────────────────
  selectedGamePk: number | null;
  setSelectedGamePk: (gamePk: number | null) => void;
  getSelectedRecommendation: () => BetRecommendation | null;
  edgePanelOpen: boolean;
  toggleEdgePanel: () => void;
  setEdgePanelOpen: (open: boolean) => void;

  // ── Phase 2: Intelligence Layer ───────────────
  agentActivity: AgentActivityEvent[];
  addAgentActivity: (event: AgentActivityEvent) => void;
  signals: IntelligenceSignal[];
  addSignal: (signal: IntelligenceSignal) => void;
  pulseScore: number | null;
  setPulseScore: (score: number) => void;
  marketVolatility: MarketVolatility | null;
  setMarketVolatility: (v: MarketVolatility) => void;
  systemHealth: SystemHealthStatus;
  setSystemHealth: (health: SystemHealthStatus) => void;
  agentFeedOpen: boolean;
  toggleAgentFeed: () => void;

  // ── Active signal filter (persisted so external nav can pre-filter signals page) ──
  activeSignalFilter: SignalFilter;
  setActiveSignalFilter: (filter: SignalFilter) => void;

  // ── Parlays ───────────────────────────────────
  parlaySlip: ParlayLeg[];
  addLegToSlip: (leg: ParlayLeg) => void;
  removeLegFromSlip: (legId: string) => void;
  clearParlaySlip: () => void;
  parlaySlipOpen: boolean;
  setParlaySlipOpen: (open: boolean) => void;
  parlays: Parlay[];
  parlaysLoading: boolean;
  fetchParlays: () => Promise<void>;
  placeParlay: (data: Parameters<typeof parlaysApi.place>[0]) => Promise<Parlay>;
  settleParlay: (
    id: string,
    result: 'won' | 'lost' | 'push' | 'void',
    legResults?: { legId: string; result: 'won' | 'lost' | 'push' | 'void' }[],
  ) => Promise<void>;

  // ── Phase 4: Mobile Intelligence OS ──────────────
  // Game workspace drawer (bottom sheet, snap to 60%/90%/full)
  workspaceDrawerOpen: boolean;
  workspaceDrawerRec: import('../types').SportRecommendation | null;
  openWorkspaceDrawer: (rec: import('../types').SportRecommendation) => void;
  closeWorkspaceDrawer: () => void;
  // Alert detail sheet (half-height)
  alertSheetOpen: boolean;
  alertSheetItem: Alert | null;
  openAlertSheet: (alert: Alert) => void;
  closeAlertSheet: () => void;
  // Quick action sheet (long press on opportunity)
  actionSheetOpen: boolean;
  actionSheetRec: import('../types').SportRecommendation | null;
  openActionSheet: (rec: import('../types').SportRecommendation) => void;
  closeActionSheet: () => void;
}

type ConnStatus = 'connected' | 'disconnected' | 'connecting';

function deriveConnectionStatus(sse: ConnStatus, rt: ConnStatus): ConnStatus {
  if (sse === 'connected' || rt === 'connected') return 'connected';
  if (sse === 'connecting' || rt === 'connecting') return 'connecting';
  return 'disconnected';
}

export const useAppStore = create<AppState>((set, get) => ({
  // ── Date ────────────────────────────────────
  selectedDate: todayPST(),
  setSelectedDate: (date) => set({ selectedDate: date }),

  // ── Games ────────────────────────────────────
  games: [],
  gamesLoading: false,
  gamesError: null,
  liveGames: {},
  fetchGames: async (date?) => {
    const d = date ?? get().selectedDate;
    set({ gamesLoading: true, gamesError: null });
    try {
      const data = await gamesApi.getSlate(d);
      set({ games: data, gamesLoading: false, lastRefreshAt: new Date().toISOString() });
    } catch (err) {
      set({ gamesError: String(err), gamesLoading: false });
    }
  },
  updateLiveGame: (data) =>
    set((state) => ({
      liveGames: { ...state.liveGames, [data.gamePk]: data },
      lastRefreshAt: new Date().toISOString(),
    })),

  // ── Recommendations ───────────────────────────
  recommendations: [],
  recsLoading: false,
  recsError: null,
  lastRecsDate: null,
  fetchRecommendations: async (date?, refresh = false) => {
    const d = date ?? get().selectedDate;
    set({ recsLoading: true, recsError: null });
    try {
      const data = await advisorApi.getRecommendations(d, refresh);
      set({
        recommendations: data,
        recsLoading: false,
        lastRecsDate: d,
        lastRefreshAt: new Date().toISOString(),
      });
    } catch (err: unknown) {
      const msg =
        err instanceof Error && err.message.includes('timeout')
          ? 'Request timed out — the analysis is taking longer than expected. Try again in a moment.'
          : err instanceof Error && err.message.includes('503')
            ? 'Backend is unavailable. Make sure the server is running.'
            : 'Failed to load recommendations. Check that the backend is running.';
      set({ recsError: msg, recsLoading: false });
    }
  },

  // ── Bets ──────────────────────────────────────
  bets: [],
  betsLoading: false,
  fetchBets: async () => {
    set({ betsLoading: true });
    try {
      const data = await betsApi.getAll();
      set({ bets: data, betsLoading: false, lastRefreshAt: new Date().toISOString() });
    } catch {
      set({ betsLoading: false });
    }
  },
  placeBet: async (data) => {
    const bet = await betsApi.place(data);
    set((state) => ({ bets: [bet, ...state.bets] }));
    await get().fetchBankroll();
    void get().fetchRecommendations(get().selectedDate, true);
    return bet;
  },
  settleBet: async (id, result, closingOdds) => {
    const bet = await betsApi.settle(id, result, closingOdds);
    set((state) => ({
      bets: state.bets.map((b) => (b.id === id ? bet : b)),
    }));
    await get().fetchBankroll();
    void get().fetchRecommendations(get().selectedDate, true);
  },
  removeBet: async (id) => {
    await betsApi.delete(id);
    set((state) => ({ bets: state.bets.filter((b) => b.id !== id) }));
    await get().fetchBankroll();
    void get().fetchRecommendations(get().selectedDate, true);
  },

  // ── Parlays ───────────────────────────────────
  parlaySlip: [],
  parlaySlipOpen: false,
  setParlaySlipOpen: (open) => set({ parlaySlipOpen: open }),
  addLegToSlip: (leg) =>
    set((state) => {
      if (state.parlaySlip.some((l) => l.id === leg.id)) return {};
      return { parlaySlip: [...state.parlaySlip, leg], parlaySlipOpen: true };
    }),
  removeLegFromSlip: (legId) =>
    set((state) => ({ parlaySlip: state.parlaySlip.filter((l) => l.id !== legId) })),
  clearParlaySlip: () => set({ parlaySlip: [], parlaySlipOpen: false }),

  parlays: [],
  parlaysLoading: false,
  fetchParlays: async () => {
    set({ parlaysLoading: true });
    try {
      const parlays = await parlaysApi.getAll();
      set({ parlays, parlaysLoading: false });
    } catch {
      set({ parlaysLoading: false });
    }
  },
  placeParlay: async (data) => {
    const parlay = await parlaysApi.place(data);
    set((state) => ({ parlays: [parlay, ...state.parlays], parlaySlip: [], parlaySlipOpen: false }));
    await get().fetchBankroll();
    return parlay;
  },
  settleParlay: async (id, result, legResults) => {
    const parlay = await parlaysApi.settle(id, result, legResults);
    set((state) => ({
      parlays: state.parlays.map((p) => (p.id === id ? parlay : p)),
    }));
    await get().fetchBankroll();
  },

  // ── Bankroll ─────────────────────────────────
  bankrollSettings: null,
  exposure: null,
  bankrollLoading: false,
  fetchBankroll: async () => {
    set({ bankrollLoading: true });
    try {
      const data = await bankrollApi.get();
      set({
        bankrollSettings: data.settings,
        exposure: data.exposure,
        bankrollLoading: false,
        lastRefreshAt: new Date().toISOString(),
      });
    } catch {
      set({ bankrollLoading: false });
    }
  },
  updateBankrollSettings: async (settings) => {
    const updated = await bankrollApi.updateSettings(settings);
    set({ bankrollSettings: updated });
    await get().fetchBankroll();
    void get().fetchRecommendations(get().selectedDate, true);
  },

  // ── Alerts ────────────────────────────────────
  alerts: [],
  unreadCount: 0,
  fetchAlerts: async () => {
    try {
      const alerts = await alertsApi.getAll();
      set({ alerts, unreadCount: alerts.filter((a) => !a.is_read).length });
    } catch {
      /* non-critical */
    }
  },
  addAlert: (alert) =>
    set((state) => ({
      alerts: [alert, ...state.alerts],
      unreadCount: state.unreadCount + (alert.is_read ? 0 : 1),
    })),
  markAlertRead: (id) => {
    alertsApi.markRead(id);
    set((state) => ({
      alerts: state.alerts.map((a) => (a.id === id ? { ...a, is_read: 1 as const } : a)),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },
  markAllAlertsRead: () => {
    alertsApi.markAllRead();
    set((state) => ({
      alerts: state.alerts.map((a) => ({ ...a, is_read: 1 as const })),
      unreadCount: 0,
    }));
  },

  // ── Watchlist ─────────────────────────────────
  watchlist: [],
  fetchWatchlist: async () => {
    try {
      const items = await watchlistApi.getAll();
      set({ watchlist: items });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[watchlist] fetchWatchlist failed', err);
    }
  },
  addToWatchlist: async (gamePk, recommendationId?) => {
    const item = await watchlistApi.add(gamePk, recommendationId);
    set((state) => {
      const filtered = state.watchlist.filter((w) => w.game_pk !== gamePk);
      return { watchlist: [item, ...filtered] };
    });
  },
  removeFromWatchlist: async (gamePk) => {
    await watchlistApi.remove(gamePk);
    set((state) => ({ watchlist: state.watchlist.filter((w) => w.game_pk !== gamePk) }));
  },
  isWatched: (gamePk) => get().watchlist.some((w) => w.game_pk === gamePk),

  // ── UI state ──────────────────────────────────
  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  connectionStatus: 'disconnected',
  sseStatus: 'disconnected',
  setSSEStatus: (status) =>
    set((s) => ({
      sseStatus: status,
      connectionStatus: deriveConnectionStatus(status, s.realtimeStatus),
    })),
  realtimeStatus: 'disconnected',
  setRealtimeStatus: (status) =>
    set((s) => ({
      realtimeStatus: status,
      connectionStatus: deriveConnectionStatus(s.sseStatus, status),
    })),
  setConnectionStatus: (status) => set({ connectionStatus: status }),
  lastRefreshAt: null,
  setLastRefreshAt: (ts) => set({ lastRefreshAt: ts }),

  // ── Mobile navigation state ───────────────────
  mobileDrawerOpen: false,
  mobileDrawerTab: 'nav',
  setMobileDrawerOpen: (open, tab) =>
    set({ mobileDrawerOpen: open, ...(tab !== undefined ? { mobileDrawerTab: tab } : {}) }),

  // ── Module / league context ───────────────────
  activeModuleId: 'diamondedge',
  activeLeagueId: 'mlb',
  activeSportId: 'baseball',
  setActiveContext: (moduleId, leagueId, sportId) =>
    set({ activeModuleId: moduleId, activeLeagueId: leagueId, activeSportId: sportId }),
  expandedGamePk: null,
  setExpandedGamePk: (gamePk) => set({ expandedGamePk: gamePk }),

  // ── Intelligence bar ──────────────────────────
  intelligenceBarCollapsed: false,
  toggleIntelligenceBar: () => set((s) => ({ intelligenceBarCollapsed: !s.intelligenceBarCollapsed })),

  // ── Intelligence Panel ────────────────────────
  selectedGamePk: null,
  setSelectedGamePk: (gamePk) => set({ selectedGamePk: gamePk }),
  getSelectedRecommendation: () => {
    const { selectedGamePk, recommendations } = get();
    if (selectedGamePk !== null) {
      return getBestGameRecommendation(selectedGamePk, recommendations) ?? null;
    }
    return sortRecommendationsByPriority(recommendations)[0] ?? null;
  },
  edgePanelOpen: false,
  toggleEdgePanel: () => set((s) => ({ edgePanelOpen: !s.edgePanelOpen })),
  setEdgePanelOpen: (open) => set({ edgePanelOpen: open }),

  // ── Phase 2: Intelligence Layer ───────────────
  agentActivity: [],
  addAgentActivity: (event) =>
    set((s) => ({
      agentActivity: [event, ...s.agentActivity].slice(0, 50),
    })),
  signals: [],
  addSignal: (signal) =>
    set((s) => ({
      signals: [signal, ...s.signals].slice(0, 100),
    })),
  pulseScore: null,
  setPulseScore: (score) => set({ pulseScore: score }),
  marketVolatility: null,
  setMarketVolatility: (v) => set({ marketVolatility: v }),
  systemHealth: { status: 'healthy' },
  setSystemHealth: (health) => set({ systemHealth: health }),
  agentFeedOpen: false,
  toggleAgentFeed: () => set((s) => ({ agentFeedOpen: !s.agentFeedOpen })),

  activeSignalFilter: {},
  setActiveSignalFilter: (filter) => set({ activeSignalFilter: filter }),

  // ── Phase 4: Mobile Intelligence OS ──────────────
  workspaceDrawerOpen: false,
  workspaceDrawerRec: null,
  openWorkspaceDrawer: (rec) => set({ workspaceDrawerOpen: true, workspaceDrawerRec: rec }),
  closeWorkspaceDrawer: () => set({ workspaceDrawerOpen: false, workspaceDrawerRec: null }),
  alertSheetOpen: false,
  alertSheetItem: null,
  openAlertSheet: (alert) => set({ alertSheetOpen: true, alertSheetItem: alert }),
  closeAlertSheet: () => set({ alertSheetOpen: false, alertSheetItem: null }),
  actionSheetOpen: false,
  actionSheetRec: null,
  openActionSheet: (rec) => set({ actionSheetOpen: true, actionSheetRec: rec }),
  closeActionSheet: () => set({ actionSheetOpen: false, actionSheetRec: null }),

  // ── User session reset ────────────────────────
  resetUserData: () =>
    set({
      recommendations: [],
      bets: [],
      bankrollSettings: null,
      exposure: null,
      alerts: [],
      unreadCount: 0,
      watchlist: [],
      games: [],
      liveGames: {},
      lastRefreshAt: null,
      selectedGamePk: null,
    }),
}));
